<template>
  <div class="barline" :class="{ 'hasVerticalValues': showValues }">
    <div class="barline_svg__wrap">
      <div class="barline_svg" :id="'barline_svg--' + this._uid"></div>
      <div v-if="showInLineValue" class="barline__single_value">{{ getFixedValue(value) }}</div>
    </div>
    <div class="barline_values" v-if="showValues">
      <div class="barline__value"><span v-if="getFixedValue(value)">{{ getFixedValue(value) }}</span><span v-else>&nbsp;</span></div>
      <div class="barline__tooltip" v-if="tooltip">
        <HelpTooltip :description="tooltip"></HelpTooltip>
      </div>
      <div :class="{ 'isDisabled': !valueExists() }" class="barline__label"><span class="barline__label__value">{{ label }}</span></div>
    </div>
  </div>
</template>

<script>
// import Vue from 'vue'
import SVG from 'svg.js'

import HelpTooltip from '../misc/HelpTooltip.vue'

import {EventBus} from '@/event-bus.js'

export default {
  name: 'barchart',
  components: { HelpTooltip },
  props: {
    color: {
      type: String,
      required: true
    },
    value: {
      type: Number,
      required: true
    },
    annotationValue: {
      type: Number
    },
    label: {
      type: String
    },
    tooltip: {
      default: false
    },
    confInt: {},
    showValues: {
      type: Boolean,
      default: false
    },
    showRange: {
      type: Boolean,
      default: false
    },
    showInLineValue: {
      type: Boolean,
      default: false
    },
    strokeWidth: {
      default: 14
    },
    canvasSize: {
      default: function () { return [20, 225] }
    }
  },
  data () {
    return {
      animationSpeed: 150,
      canvas: false
    }
  },
  watch: {
    confInt () {
      this.reset()
      this.draw()
    },
    value () {
      this.reset()
      this.draw()
    },
    annotationValue () {
      this.reset()
      this.draw()
    },
    confidenceIntervals () {
      this.reset()
      this.draw()
    },
    showRange () {
      this.reset()
      this.draw()
    }
  },
  mounted () {
    this.drawCanvas()
    this.draw()
  },
  destroyed () {
    EventBus.$emit('tooltip.hide')
  },
  methods: {
    valueExists () {
      return this.value || this.value === 0 || this.value === 0.00
    },
    reset () {
      this.removeMouseHover(this.canvas)
      if (this.canvas) {
        this.canvas.remove()
      }
      this.drawCanvas()
    },
    drawCanvas () {
      let visibleWidth = 0
      if (this.showValues && !this.showInLineValue) {
        visibleWidth = 20
      } else if (!this.showValues && this.showInLineValue) {
        visibleWidth = 40
      } else {
        visibleWidth = 10
      }
      this.canvas = SVG('barline_svg--' + this._uid)
      this.canvas.viewbox(0, 0, this.canvasSize[0], this.canvasSize[1])
      this.canvas.style({ width: visibleWidth + 'px', margin: '0 auto', display: 'block' })
      this.addMouseHover(this.canvas)
    },
    draw () {
      let cw = this.canvasSize[0]
      let ch = this.canvasSize[1]

      let x = cw / 2
      let y = ch - (ch * this.value)
      let startX = (cw - this.strokeWidth) / 2

      this.drawLine(x, 0, x, ch, 'svg-stroke--gray-lighter', this.strokeWidth, false)

      if (this.valueExists()) {
        // let lwidth = this.annotationValue ? this.strokeWidth * 0.75 : this.strokeWidth
        let lwidth = this.strokeWidth
        let lx = startX + (lwidth / 2)
        this.drawLine(lx, ch, lx, y, 'svg-stroke--' + this.color, lwidth)
      }

      if (this.showRange) {
        this.drawCI()
      }

      if (this.annotationValue) {
        let ay = ch - (ch * this.annotationValue)
        let awidth = 2
        this.drawLine(0, ay, cw, ay, 'svg-stroke--annotation', awidth)

        /*
        let ay = ch - (ch * this.annotationValue)
        let awidth = (this.strokeWidth * 0.3)
        let ax = cw - startX - (awidth / 2)
        this.drawLine(ax, ay, ax, ch, 'svg-stroke--annotation', awidth)
        */
      }
    },

    drawCI () {
      let cw = this.canvasSize[0]
      let ch = this.canvasSize[1]
      let x = cw / 2

      let confY1 = ch - (ch * this.confInt[0])
      let confY2 = ch - (ch * this.confInt[1])

      this.drawLine(x, confY1, x, confY2, 'svg-stroke--' + this.color + ' svg-stroke--faded', cw)
    },

    addMouseHover (elem) {
      let mouseOverObj = {}

      if (this.value || this.value === 0 || this.value === 0.00) {
        mouseOverObj = {
          style: 'map',
          label: this.label,
          value: this.getFixedValue(this.value)
        }
      } else {
        mouseOverObj = {
          style: 'single',
          label: this.$Strings('barLineNoValue').replace('%label', this.label)
        }
      }

      // Mouse over actions
      elem.mouseover(() => {
        EventBus.$emit('tooltip.show', mouseOverObj)
      })
      elem.mouseout(() => {
        EventBus.$emit('tooltip.hide')
      })
    },

    removeMouseHover (elem) {
      elem.off()
    },

    /**
     * Draw a line in the chart
     * @param  {integer} xPos      The start x position
     * @param  {integer} yPos      The start y position
     * @param  {integer} xPos2     The end x position
     * @param  {integer} yPos2     The end y position
     * @param  {String} className  The classname
     * @param  {boolean/integer} animate  The speed of animation if any
     * @return {Object} The line object
     */
    drawLine (xPos, yPos, xPos2, yPos2, className, strokeWidth = this.strokeWidth, animationSpeed = this.animationSpeed) {
      let lineLength = yPos - yPos2

      let line = this.canvas
        .line(xPos, yPos, xPos2, yPos2)
        .stroke({ width: strokeWidth, dasharray: '0,' + lineLength })

      line.addClass(className)

      if (animationSpeed !== false) {
        line.animate(animationSpeed, '>').stroke({ dasharray: lineLength + ',' + lineLength })
      } else {
        line.stroke({ dasharray: (lineLength * -1) + ',' + (lineLength * -1) })
      }
      return line
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~@/components/sass/shared/colors';

  .barline.hasVerticalValues{
    overflow: hidden;
    min-height: 490px;
  }

  .barline_svg__wrap{
    position: relative;
  }

  .barline__single_value{
    color: $white;
    font-size: 9px;
    font-weight: 600;
    position: absolute;
    bottom: 5px;
    left: 0;
    text-align: center;
    width: 100%;
  }

  .barline__svg{}
  .barline__value{
    font-size: 8px;
    font-weight: bold;
    margin: 5px 0;
    text-align: center;

    @media (max-width: 1100px) {
      font-size: 7.5px;
    }
  }
  .barline__tooltip{
    text-align: center;
  }
  .barline__label{ position: relative; }
  .barline__label__value{
    display: block;
    font-size: 12px;
    height: 20px;
    left: 50%;
    line-height: 1;
    margin-left: -6px; // fontsize / 2
    margin-top: 310px; // width + 10px space up to the label above
    position: absolute;
    text-align: right;
    top: 0;
    transform: rotate(-90deg);
    transform-origin: left top 0;
    white-space: nowrap;
    width: 300px;
  }
  .barline__label.isDisabled .barline__label__value{
    color: $gray-lighter;
  }
</style>



// WEBPACK FOOTER //
// src/components/shared/charts/BarLine.vue